import React, { useState } from 'react'
import api from 'helpers/api'
import { formatShortDateTime } from 'helpers/date'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import Button from 'components/button'
import Dialog from 'components/dialog'
import RadioGroup from 'components/radio-group'
import { BLUE_BUTTON } from 'components/save-buttons'

const DURATIONS = [
  { value: '24', label: '24 hours' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' }
]

/** When access granted now for so many hours would end. */
const endOf = (hours: string) => new Date(Date.now() + Number(hours) * 60 * 60 * 1000)

/** Grants access, in our dialog: how long for, as choice cards each saying when it would end. */
const GrantAccessDialog: React.FC<{ onClose: () => void; onGrant: (expiresAt: string) => Promise<void> }> = ({
  onClose,
  onGrant
}) => {
  const [hours, setHours] = useState(DURATIONS[0].value)
  const [saving, setSaving] = useState(false)

  const grant = async () => {
    setSaving(true)
    const expiresAt = endOf(hours).toISOString()
    try {
      await onGrant(expiresAt)
    } catch {
      setSaving(false)
      return
    }
    setSaving(false)
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && !saving && onClose()}
      title="Grant Temporary Access"
      description="The Morta Support team can sign in to this account to help with an issue until the time chosen. Access can be revoked at any time before then."
      footer={
        <>
          <Button internalType="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button internalType="outline" className={BLUE_BUTTON} isLoading={saving} onClick={grant}>
            Grant Access
          </Button>
        </>
      }
    >
      <RadioGroup
        label="Access for"
        options={DURATIONS.map((each) => ({
          ...each,
          description: `Until ${formatShortDateTime(endOf(each.value).toISOString())}`
        }))}
        value={hours}
        onChange={setHours}
      />
    </Dialog>
  )
}

/**
 * Morta Support access, as the footer of the Security card: what it is, with Grant Temporary Access; or until
 * when it's granted, with Revoke Access.
 */
const SupportAccess: React.FC = () => {
  const { user, updateUser } = useAuth()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [granting, setGranting] = useState(false)
  const [revoking, setRevoking] = useState(false)

  // Rejects when it fails, having said why, so the dialog stays open to try again.
  const setAccess = async (expiresAt: string | null) => {
    if (!user) return
    try {
      await api.updateUserAccount(undefined, undefined, undefined, undefined, expiresAt)
    } catch (error) {
      displayErrorMessage(error)
      throw error
    }
    await updateUser({ allowSupportAccess: expiresAt !== null, allowSupportAccessExpiresAt: expiresAt })
    setSnackbarMessage({
      status: 'success',
      message: expiresAt
        ? 'The Morta Support team was given access to this account'
        : 'The Morta Support team can no longer access this account'
    })
  }

  const revoke = () => {
    setRevoking(true)
    setAccess(null)
      .catch(() => undefined)
      .then(() => setRevoking(false))
  }

  if (!user) return null

  if (user.allowSupportAccess)
    return (
      <>
        <p className="flex-1 text-muted">
          The Morta Support team can sign in to this account
          {user.allowSupportAccessExpiresAt
            ? ` until ${formatShortDateTime(user.allowSupportAccessExpiresAt)}.`
            : ' until access is revoked.'}
        </p>
        <Button internalType="outline" isLoading={revoking} onClick={revoke}>
          Revoke Access
        </Button>
      </>
    )

  return (
    <>
      <p className="flex-1 text-muted">Let the Morta Support team sign in to this account to help with an issue.</p>
      <Button internalType="outline" onClick={() => setGranting(true)}>
        Grant Temporary Access
      </Button>
      {granting && <GrantAccessDialog onClose={() => setGranting(false)} onGrant={setAccess} />}
    </>
  )
}

export default SupportAccess
